import { useState } from 'react';
import {
  Card,
  CardHeader,
  Badge,
  Button,
  Input,
  Select,
  Pagination,
  LoadingSkeleton,
} from '@nexora/design-system';
import { useIbsCbs } from './useIbsCbs';
import { EMPTY } from '../../../shared/utils/format';

/* ───────────────────────────────────────────────────────────────────────────
   Portado de base-html, slug `erpone_motor_de_c_lculo_ibs_cbs_integrado`
   → Motor de Cálculo IBS/CBS Integrado — GET /fiscal/ibs-cbs.

   Da referência vieram: a tabela por NCM com as alíquotas legadas
   (ICMS/PIS/COFINS) e as estimadas (IBS/CBS), o seletor de ano, e o
   comparativo de transição da reforma (2026→2033).

   Não vieram (não existem no contrato): projeção de NF-e por NCM em
   quantidade/valor, nem o drill-down por item.

   A tabela compara a carga antiga (ICMS+PIS+COFINS) com a nova (IBS+CBS)
   considerando o ano de transição selecionado — `transitionPercent` vem do
   backend já em percentual (100 = 100%).
   ─────────────────────────────────────────────────────────────────────────── */

const UFS = [
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS',
  'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC',
  'SE', 'SP', 'TO',
];

const YEARS = [2026, 2027, 2028, 2029, 2030, 2031, 2032, 2033];

function formatPct(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return EMPTY;
  return `${Number(v).toFixed(1).replace('.', ',')}%`;
}

const PAGE_SIZE = 20;

export function IbsCbsPage(): JSX.Element {
  const [year, setYear] = useState(2026);
  const [uf, setUf] = useState('SP');
  const [ncmPrefix, setNcmPrefix] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading, error, refetch } = useIbsCbs({
    year,
    uf,
    ncmPrefix: ncmPrefix || undefined,
    page,
    limit: PAGE_SIZE,
  });

  const lines = data?.data ?? [];
  const comparativo = data?.transitionComparativo ?? [];
  const totalPages = Math.max(1, data?.totalPages ?? 1);

  return (
    <div className="ibscbs-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
            Motor de Cálculo IBS/CBS Integrado
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Estimativa da carga IBS/CBS por NCM na transição da reforma tributária, comparada às alíquotas legadas.
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card padding="1rem" variant="outlined" className="filter-card">
        <div className="ibscbs-filters">
          <Select label="Ano da transição" value={String(year)} onChange={(e) => { setYear(Number(e.target.value)); setPage(1); }}>
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </Select>
          <Select label="UF de referência" value={uf} onChange={(e) => { setUf(e.target.value); setPage(1); }}>
            {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
          </Select>
          <Input
            label="Prefixo NCM"
            placeholder="Ex.: 8471"
            value={ncmPrefix}
            onChange={(e) => { setNcmPrefix(e.target.value.replace(/\D/g, '')); setPage(1); }}
          />
        </div>
      </Card>

      {/* Transition comparison */}
      {!isLoading && !error && comparativo.length > 0 && (
        <Card variant="outlined" padding="0" className="ibscbs-transition">
          <CardHeader className="px-md pt-md">
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Comparativo de transição ({comparativo[0]?.year ?? '—'} → {comparativo[comparativo.length - 1]?.year ?? '—'})</h3>
          </CardHeader>
          <div style={{ overflowX: 'auto' }}>
            <table className="fiscal-table">
              <thead>
                <tr>
                  <th>Ano</th>
                  <th align="right">Transição</th>
                  <th align="right">IBS est.</th>
                  <th align="right">CBS est.</th>
                  <th align="right">Total novo</th>
                </tr>
              </thead>
              <tbody>
                {comparativo.map((p) => {
                  const atual = p.year === data?.year;
                  return (
                    <tr key={p.year} className={atual ? 'ibscbs-transition__atual' : undefined}>
                      <td><strong>{p.year}</strong>{atual && <Badge size="sm" variant="info" className="ibscbs-transition__tag">atual</Badge>}</td>
                      <td align="right" className="tabular-nums">{formatPct(p.transitionPercent)}</td>
                      <td align="right" className="tabular-nums">{formatPct(p.ibsEstPct)}</td>
                      <td align="right" className="tabular-nums">{formatPct(p.cbsEstPct)}</td>
                      <td align="right" className="tabular-nums">{formatPct(p.ibsEstPct + p.cbsEstPct)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* NCM table */}
      <Card variant="outlined" padding="0">
        <CardHeader className="px-md pt-md" action={
          <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
            {data?.total ?? 0} NCMs
          </span>
        }>
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Estimativa IBS/CBS por NCM — {data?.year ?? year}</h3>
        </CardHeader>
        {isLoading ? (
          <div className="ibscbs-loading">
            <LoadingSkeleton count={6} height="48px" />
          </div>
        ) : error ? (
          <div className="ibscbs-error">
            <p className="m-0" style={{ fontWeight: 600, color: '#ba1a1a' }}>Erro ao carregar estimativas</p>
            <p className="m-0" style={{ fontSize: '0.875rem', color: '#74777f' }}>
              {error instanceof Error ? error.message : 'Erro desconhecido'}
            </p>
            <Button variant="primary" onClick={() => refetch()}>Tentar novamente</Button>
          </div>
        ) : lines.length === 0 ? (
          <div className="ibscbs-empty">
            <p className="m-0">Nenhum NCM encontrado para o filtro informado.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="fiscal-table">
              <thead>
                <tr>
                  <th>NCM</th>
                  <th>Descrição</th>
                  <th align="right">ICMS</th>
                  <th align="right">PIS</th>
                  <th align="right">COFINS</th>
                  <th align="right">Legado total</th>
                  <th align="right">IBS est.</th>
                  <th align="right">CBS est.</th>
                  <th align="right">Novo total</th>
                  <th>Observações</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.ncm}>
                    <td className="tabular-nums"><strong>{l.ncm}</strong></td>
                    <td style={{ minWidth: 180 }}>{l.descricao}</td>
                    <td align="right" className="tabular-nums">{formatPct(l.icmsAliquota)}</td>
                    <td align="right" className="tabular-nums">{formatPct(l.pisAliquota)}</td>
                    <td align="right" className="tabular-nums">{formatPct(l.cofinsAliquota)}</td>
                    <td align="right" className="tabular-nums">{formatPct(l.icmsPisCofinsTotalPct)}</td>
                    <td align="right" className="tabular-nums">{formatPct(l.ibsEstPct)}</td>
                    <td align="right" className="tabular-nums">{formatPct(l.cbsEstPct)}</td>
                    <td align="right" className="tabular-nums"><strong>{formatPct(l.ibsCbsTotalPct)}</strong></td>
                    <td>
                      {l.acoes.length === 0 ? <span className="cell-muted">—</span> : (
                        <div className="ibscbs-acoes">
                          {l.acoes.map((a) => <Badge key={a} size="sm" variant="neutral">{a}</Badge>)}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && !isLoading && !error && (
          <div className="ibscbs-pagination">
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        )}
      </Card>

      <style>{`
        .ibscbs-page { padding: 1.5rem; }
        .ibscbs-filters { display: flex; gap: 0.75rem; align-items: flex-end; flex-wrap: wrap; }
        .ibscbs-filters > * { flex: 1; min-width: 160px; }
        .ibscbs-transition { margin-bottom: 1rem; }
        .ibscbs-transition__atual { background: #f4f8ff; }
        .ibscbs-transition__tag { margin-left: 0.5rem; }
        .ibscbs-loading, .ibscbs-error, .ibscbs-empty { padding: 2rem; text-align: center; display: grid; gap: 0.5rem; justify-items: center; color: #74777f; }
        .ibscbs-error { color: #ba1a1a; }
        .ibscbs-acoes { display: flex; flex-wrap: wrap; gap: 0.25rem; max-width: 22ch; }
        .ibscbs-pagination { display: flex; justify-content: flex-end; padding: 0.75rem 1rem; }
        .fiscal-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .fiscal-table th {
          text-align: left; padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e;
          font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em;
          border-bottom: 2px solid #e0e3e5; white-space: nowrap; position: sticky; top: 0; z-index: 1;
        }
        .fiscal-table td { padding: 0.625rem 1rem; border-bottom: 1px solid #eceef0; vertical-align: middle; }
        .fiscal-table tbody tr:hover { background: #f8fafc; }
        .tabular-nums { font-variant-numeric: tabular-nums; }
        .cell-muted { color: #74777f; }
      `}</style>
    </div>
  );
}